import { FC } from 'react';
import { useSelector } from 'react-redux';
import { graphql, useFragment } from 'react-relay';
import { useAuctionPricing } from 'dibs-auctions/exports/hooks';

// components
import { FormattedMessage } from 'dibs-react-intl';
import { PriceDisplay, FormattedPrice } from 'dibs-buyer-price';
import { PdpSharedPurchaseButton } from '../PdpSharedButtons/PdpSharedPurchaseButton';
import { PdpSharedAuctionBidButton } from '../PdpSharedAuctionBidButton/PdpSharedAuctionBidButton';
import { PdpSharedAuctionTooltipLazy } from '../PdpSharedAuctionToolTip/PdpSharedAuctionTooltipLazy';
import { OriginalListPrice } from 'dibs-auctions/exports/OriginalListPrice';

// types
import { PdpSharedAuctionActionBinAndBid_item$key as Item } from '../PdpSharedAuctionActions/__generated__/PdpSharedAuctionActionBinAndBid_item.graphql';
import { PdpSharedAuctionActionBinAndBid_viewer$key as Viewer } from '../PdpSharedAuctionActions/__generated__/PdpSharedAuctionActionBinAndBid_viewer.graphql';
import { OnAuctionBidClick } from '../PdpSharedAuctionInfo/PdpSharedAuctionInfo';

// styles
import styles from './PdpSharedAuctionActionBinAndBid.scss';

type Props = {
    item: Item;
    viewer: Viewer;
    currency: string;
    isBiddingDisabled?: boolean;
    onAuctionBidClick: OnAuctionBidClick;
};

type RelayVariables = {
    isClient: boolean;
    userId: string;
    itemId: string;
};

type ReduxStoreType = {
    relayVariables: {
        variables: RelayVariables;
    };
};

const itemFragment = graphql`
    fragment PdpSharedAuctionActionBinAndBid_item on Item {
        ...PriceDisplay_item
            @arguments(userId: $userId, isTrade: $isTrade, userCountryCode: $userCountryCode)
        ...useAuctionPricing_item @arguments(page: pdp)
        ...PdpSharedPurchaseButton_item
        ...OriginalListPrice_item
        relevantAuctionSolution(page: pdp) {
            hasReserve
        }
    }
`;

const viewerFragment = graphql`
    fragment PdpSharedAuctionActionBinAndBid_viewer on Viewer {
        originalListPricePdpFF: featureFlag(feature: "originalListPricePdp")
    }
`;

export const PdpSharedAuctionActionBinAndBid: FC<Props> = ({
    item: itemRef,
    viewer: viewerRef,
    currency,
    onAuctionBidClick,
    isBiddingDisabled,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const viewer = useFragment(viewerFragment, viewerRef);
    const { hasReserve } = item?.relevantAuctionSolution || {};
    const { buyItNowAmount } = useAuctionPricing({ item, currency });
    const { userId, itemId } = useSelector<ReduxStoreType, RelayVariables>(
        relay => relay?.relayVariables?.variables || {}
    );
    const originalListPricePdpFF = viewer?.originalListPricePdpFF;

    return (
        <>
            {originalListPricePdpFF && (
                <OriginalListPrice
                    item={item}
                    currency={currency}
                    className={styles.originalListPrice}
                />
            )}
            {!hasReserve && (
                <span data-tn="pdp-item-pricing-label-no-reserve" className={styles.noReserve}>
                    <FormattedMessage
                        id="PdpRespAuctionActionBinAndBid.noReserve"
                        defaultMessage="No Reserve"
                    />
                    <PdpSharedAuctionTooltipLazy type="reserve" />
                </span>
            )}
            <div className={styles.container}>
                <div className={styles.priceRow}>
                    <span data-tn="pdp-item-pricing-label-starting-bid" className={styles.boldCopy}>
                        <FormattedMessage
                            id="PdpRespAuctionActionBinAndBid.startingBid"
                            defaultMessage="Starting Bid"
                        />
                    </span>
                    <PriceDisplay currency={currency} item={item} sku={null} useSvgIcon />
                </div>
                <PdpSharedAuctionBidButton
                    className={styles.priceButton}
                    onClick={() => onAuctionBidClick()}
                    disabled={isBiddingDisabled}
                />

                <div className={styles.priceRow}>
                    <span data-tn="pdp-item-pricing-label-buy-it-now" className={styles.boldCopy}>
                        <FormattedMessage
                            id="PdpRespAuctionActionBinAndBid.buyItNow"
                            defaultMessage="Buy It Now"
                        />
                        <PdpSharedAuctionTooltipLazy type="buyItNow" />
                    </span>
                    <div data-tn="pdp-item-buy-it-now-price" className={styles.price}>
                        {buyItNowAmount && (
                            <FormattedPrice value={buyItNowAmount} currency={currency} />
                        )}
                    </div>
                </div>
                <PdpSharedPurchaseButton
                    item={item}
                    itemId={itemId}
                    userId={userId}
                    isPrimary={false}
                    quantitySelected={'1'}
                    disabled={isBiddingDisabled}
                />
            </div>
        </>
    );
};
